import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axiosInstance from '../../../services/AxiosInstance';
import Api from '../../../constants/Api';
import Module from '../../../constants/Module';
import CashFlow, { getDueSoonLimitDate } from '../../../constants/CashFlow';
import LocalStorageType from '../../../constants/LocalStorageType';
import MenuConfig from '../../../constants/MenuConfig';
import { SearchOperation } from '../../../utils/ApiUtils';
import { formatDateToYYYYMMDD } from '../../../utils/DateUtils';
import { convertDoubleToCurrencyPTBR } from '../../../utils/ConverterUtils';
import { hasMenuAccess } from '../../../utils/ModuleAccessUtils';

const PayableDueAlert = () => {
  const [summary, setSummary] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const alertRef = useRef(null);
  const location = useLocation();

  // Na própria tela de contas a pagar e receber o aviso não tem para onde levar: os
  // cartões de "a vencer" e "vencidos" mostram a mesma informação, com mais detalhe e
  // já respeitando os filtros da tela.
  const onAccountsScreen = location.pathname.startsWith(MenuConfig.cash_flow.payable_receivable.route);

  const publishHeight = useCallback(() => {
    const height = alertRef.current ? alertRef.current.offsetHeight : 0;
    document.body.style.setProperty('--payable-alert-h', `${height}px`);
  }, []);

  useEffect(() => {
    // Na tela de contas o aviso não aparece, então nem vale consultar.
    if (onAccountsScreen) return undefined;

    let user;
    try { user = JSON.parse(localStorage.getItem(LocalStorageType.USER_DETAILS)); } catch { return undefined; }
    // O módulo sozinho não basta: o usuário pode ter acesso ao caixa, categorias ou
    // DRE sem permissão para abrir a tela de contas a pagar e receber.
    if (!user || !hasMenuAccess(Module.CASH_FLOW, 'payable_receivable')) return undefined;

    // O lembrete cobre a semana seguinte para dar tempo de programar o pagamento,
    // sem transformar contas já vencidas em uma cobrança diferente nesta faixa.
    //
    // As datas saem no fuso local, igual ao link "Ver contas". Com toISOString (UTC), depois
    // das 21h "hoje" virava amanhã: a conta que vence no dia sumia do aviso e o número não
    // batia com o da tela que o botão abre.
    const today = new Date();
    const end = getDueSoonLimitDate();
    const filters = [
      { field: 'type', value: 'PAYABLE', operation: SearchOperation.EQUAL },
      { field: 'paid', value: false, operation: SearchOperation.EQUAL },
      { field: 'dueDate', value: formatDateToYYYYMMDD(today), operation: SearchOperation.GREATER_THAN_EQUAL },
      { field: 'dueDate', value: formatDateToYYYYMMDD(end), operation: SearchOperation.LESS_THAN_EQUAL },
    ];
    axiosInstance.post(`${Api.cash_flow.payable_receivable}/pageable`, {
      filters, page: 0, size: 10000, orders: [{ field: 'dueDate', direction: 'ASC' }]
    }).then(({ data }) => {
      const rows = data.content || [];
      // O total é apenas informativo; a quitação continua sendo feita na tela de
      // contas, acessada pelo botão do alerta.
      // A lista vem por vencimento crescente, então a primeira linha é o próximo vencimento.
      if (rows.length) setSummary({
        count: rows.length,
        total: rows.reduce((sum, row) => sum + Number(row.value || 0), 0),
        nextDueDate: rows[0].dueDate
      });
    }).catch(() => {});
    return undefined;
  }, [onAccountsScreen]);

  const visible = Boolean(summary) && !dismissed && !onAccountsScreen;

  useEffect(() => {
    if (!visible) {
      document.body.classList.remove('payable-alert-active');
      document.body.style.removeProperty('--payable-alert-h');
      return undefined;
    }

    // O alerta fica fora do fluxo do conteúdo, então o cabeçalho e a página precisam
    // conhecer sua altura real para não ficarem escondidos atrás da faixa fixa.
    document.body.classList.add('payable-alert-active');
    publishHeight();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publishHeight) : null;
    if (observer && alertRef.current) observer.observe(alertRef.current);
    window.addEventListener('resize', publishHeight);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', publishHeight);
      document.body.classList.remove('payable-alert-active');
      document.body.style.removeProperty('--payable-alert-h');
    };
  }, [visible, publishHeight]);

  if (!visible) return null;
  const value = convertDoubleToCurrencyPTBR(summary.total);
  const today = new Date();
  const end = getDueSoonLimitDate();
  const accountsUrl = `/cash-flow/accounts-payable-receivable?alert=payable&from=${formatDateToYYYYMMDD(today)}&to=${formatDateToYYYYMMDD(end)}`;
  const next = describeNextDue(summary.nextDueDate, today);
  const plural = summary.count !== 1;
  // Uma frase só, com o dinheiro em destaque: "Atenção financeira" e "no período" repetiam o
  // que o ícone e "próximos 7 dias" já dizem, e ocupavam uma segunda linha em toda tela.
  return <div className={`payable-due-alert${next.today ? ' is-due-today' : ''}`} role="alert" ref={alertRef}>
    <div className="payable-due-alert__icon" aria-hidden="true">
      <i className="fa-solid fa-calendar-day" />
    </div>
    <p className="payable-due-alert__message">
      <span className="payable-due-alert__summary">
        <strong>{summary.count} {plural ? 'contas a pagar' : 'conta a pagar'}</strong>
        {' '}{plural ? 'vencem' : 'vence'} nos próximos {CashFlow.DUE_SOON_DAYS}{'\u00a0'}dias:
        {' '}<strong className="payable-due-alert__total">{value}</strong>
      </span>
      {next.label && (
        <span className="payable-due-alert__next">
          Próximo vencimento: <strong>{next.label}</strong>
        </span>
      )}
    </p>
    <div className="payable-due-alert__actions">
      {/* A tela lê estes parâmetros e executa a busca já filtrada para o período do alerta. */}
      <Link className="payable-due-alert__link" to={accountsUrl}>
        Ver contas <i className="fa-solid fa-arrow-right" aria-hidden="true" />
      </Link>
      {/* Botão explícito para o X não depender da imagem de fundo do Bootstrap. */}
      <button type="button" className="payable-due-alert__close" aria-label="Fechar" onClick={() => setDismissed(true)}>
        <i className="fa-solid fa-xmark" aria-hidden="true" />
      </button>
    </div>
  </div>;
};

/**
 * Próximo vencimento em palavras: "hoje", "amanhã" ou a data (dd/mm).
 *
 * É o dado que decide se o pagamento é para agora ou para a semana; o total sozinho não diz.
 * A data chega como "yyyy-MM-dd" e é montada no fuso local, porque new Date("2026-10-04")
 * seria meia-noite UTC, e no Brasil isso cai no dia anterior.
 */
const describeNextDue = (dueDate, today) => {
  if (!dueDate) return { label: null, today: false };
  const [year, month, day] = dueDate.split('-').map(Number);
  const due = new Date(year, month - 1, day);
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((due - start) / 86400000);
  if (days <= 0) return { label: 'hoje', today: true };
  if (days === 1) return { label: 'amanhã', today: false };
  return { label: `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}`, today: false };
};

export default PayableDueAlert;
